import { useState, useRef } from "react";
import { Sparkles, Loader2, Image as ImageIcon, Camera, Wand2, X, Mic, Square } from "lucide-react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

// Voice-to-text button. Records audio and appends the transcript via onText(text).
export function VoiceButton({ onText, testid = "voice-btn" }) {
  const t = useT();
  const [recording, setRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((tr) => tr.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setBusy(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "opname.webm");
          const { data } = await api.post("/transcribe", fd);
          if (data.text?.trim()) { onText(data.text.trim()); toast.success(t("Tekst toegevoegd")); }
          else toast.info(t("Niets verstaan, probeer opnieuw"));
        } catch { toast.error(t("Transcriptie mislukt")); }
        finally { setBusy(false); }
      };
      rec.start();
      recorderRef.current = rec;
      setRecording(true);
    } catch { toast.error(t("Geen microfoon-toegang")); }
  };
  const stop = () => { recorderRef.current?.stop(); setRecording(false); };

  return (
    <button type="button" data-testid={testid} onClick={recording ? stop : start} disabled={busy}
      className={`brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold flex items-center gap-1 ${recording ? "bg-red-500 text-white animate-pulse" : "bg-brand-blue text-white"}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : recording ? <Square className="w-3.5 h-3.5" strokeWidth={3} /> : <Mic className="w-3.5 h-3.5" strokeWidth={2.5} />}
      {busy ? t("Bezig") : recording ? t("Stop") : t("Inspreken")}
    </button>
  );
}

// Multiple photo picker (camera + gallery). value = array of url strings.
export function MultiPhotoInput({ value = [], onChange, max = 5, testid = "multi-photo" }) {
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const srcOf = (u) => (u?.startsWith("http") ? u : `${backend}${u}`);

  const upload = async (file) => {
    if (!file) return;
    if (value.length >= max) return toast.error(`Maximaal ${max} foto's`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange([...value, data.url]);
      toast.success("Foto toegevoegd");
    } catch { toast.error("Upload mislukt"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={testid}>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {value.map((u, i) => (
            <div key={i} className="relative">
              <img src={srcOf(u)} alt="foto" className="w-20 h-20 object-cover brutal-sm rounded-xl" data-testid={`${testid}-thumb-${i}`} />
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))}
                className="absolute -top-1.5 -right-1.5 brutal-sm brutal-press rounded-full p-1 bg-white" data-testid={`${testid}-remove-${i}`}><X className="w-3.5 h-3.5" strokeWidth={3} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-camera`} onClick={() => cameraRef.current?.click()} disabled={busy}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" strokeWidth={2.5} />}Camera</button>
        <button type="button" data-testid={`${testid}-gallery`} onClick={() => galleryRef.current?.click()} disabled={busy}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5"><ImageIcon className="w-4 h-4" strokeWidth={2.5} />Galerij</button>
      </div>
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} />
    </div>
  );
}


// "✨ AI herschrijf" button attached to any description/toelichting field.
export function AiRewriteButton({ value, onChange, context, testid = "ai-rewrite" }) {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    if (!value?.trim()) return toast.error("Typ eerst iets om te herschrijven");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/rewrite", { text: value, context });
      onChange(data.text);
      toast.success("Herschreven met AI ✨");
    } catch { toast.error("AI-herschrijven mislukt"); }
    finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testid} onClick={run} disabled={busy}
      className="brutal-sm brutal-press rounded-full px-2.5 py-1 text-xs font-bold bg-brand-violet text-white flex items-center gap-1">
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2.5} />}AI
    </button>
  );
}

// Photo picker: gallery, camera or AI-generated. value = url string.
export function PhotoInput({ value, onChange, aiPrompt = "", testid = "photo-input", square = false, initials = "" }) {
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      onChange(data.url);
      toast.success("Foto toegevoegd");
    } catch { toast.error("Upload mislukt"); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error("Vul eerst een titel/omschrijving in voor de AI");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: aiPrompt });
      onChange(data.url);
      toast.success("AI-afbeelding gemaakt ✨");
    } catch { toast.error("AI-afbeelding mislukt"); }
    finally { setBusy(false); }
  };

  const backend = process.env.REACT_APP_BACKEND_URL;
  const src = value ? (value.startsWith("http") ? value : `${backend}${value}`) : null;

  return (
    <div data-testid={testid}>
      {src ? (
        <div className={`relative mb-2 ${square ? "w-28 h-28" : ""}`}>
          <img src={src} alt="foto" className={square ? "w-28 h-28 object-cover brutal-sm rounded-2xl" : "w-full h-40 object-cover brutal-sm rounded-xl"} data-testid={`${testid}-preview`} />
          <button type="button" onClick={() => onChange(null)} className="absolute -top-1.5 -right-1.5 brutal-sm brutal-press rounded-full p-1.5 bg-white"><X className="w-4 h-4" strokeWidth={3} /></button>
        </div>
      ) : square ? (
        <div className="w-28 h-28 brutal-sm rounded-2xl bg-brand-violet text-white flex items-center justify-center font-head font-black text-3xl mb-2" data-testid={`${testid}-initials`}>{initials || "?"}</div>
      ) : null}
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-gallery`} onClick={() => galleryRef.current?.click()} disabled={busy}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5"><ImageIcon className="w-4 h-4" strokeWidth={2.5} />Galerij</button>
        <button type="button" data-testid={`${testid}-camera`} onClick={() => cameraRef.current?.click()} disabled={busy}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5"><Camera className="w-4 h-4" strokeWidth={2.5} />Camera</button>
        <button type="button" data-testid={`${testid}-ai`} onClick={aiGenerate} disabled={busy}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-brand-violet text-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" strokeWidth={2.5} />}AI
        </button>
      </div>
      <input ref={galleryRef} type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
    </div>
  );
}

export function PhotosInput({ value = [], onChange, aiPrompt = "", testid = "photos-input", max = 8 }) {
  const [busy, setBusy] = useState(false);
  const galleryRef = useRef(null);
  const cameraRef = useRef(null);
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  const backend = process.env.REACT_APP_BACKEND_URL;
  const srcOf = (u) => (u ? (u.startsWith("http") ? u : `${backend}${u}`) : null);

  const uploadMany = async (files) => {
    if (!files || !files.length) return;
    setBusy(true);
    try {
      const arr = [...files].slice(0, Math.max(0, max - list.length));
      const urls = [];
      for (const f of arr) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd);
        urls.push(data.url);
      }
      if (urls.length) onChange([...list, ...urls]);
      toast.success(urls.length > 1 ? `${urls.length} foto's toegevoegd` : "Foto toegevoegd");
    } catch { toast.error("Upload mislukt"); }
    finally { setBusy(false); }
  };

  const aiGenerate = async () => {
    if (!aiPrompt.trim()) return toast.error("Vul eerst een titel/omschrijving in voor de AI");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/image", { prompt: aiPrompt });
      onChange([...list, data.url]);
      toast.success("AI-afbeelding gemaakt ✨");
    } catch { toast.error("AI-afbeelding mislukt"); }
    finally { setBusy(false); }
  };

  const removeAt = (i) => onChange(list.filter((_, j) => j !== i));
  const full = list.length >= max;

  return (
    <div data-testid={testid}>
      {list.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-2">
          {list.map((u, i) => (
            <div key={i} className="relative">
              <img src={srcOf(u)} alt="foto" className="w-full h-24 object-cover brutal-sm rounded-xl" data-testid={`${testid}-preview-${i}`} />
              <button type="button" onClick={() => removeAt(i)} data-testid={`${testid}-remove-${i}`} className="absolute -top-1.5 -right-1.5 brutal-sm brutal-press rounded-full p-1 bg-white"><X className="w-3.5 h-3.5" strokeWidth={3} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <button type="button" data-testid={`${testid}-gallery`} onClick={() => galleryRef.current?.click()} disabled={busy || full}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"><ImageIcon className="w-4 h-4" strokeWidth={2.5} />Galerij</button>
        <button type="button" data-testid={`${testid}-camera`} onClick={() => cameraRef.current?.click()} disabled={busy || full}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50"><Camera className="w-4 h-4" strokeWidth={2.5} />Camera</button>
        <button type="button" data-testid={`${testid}-ai`} onClick={aiGenerate} disabled={busy || full}
          className="flex-1 brutal-sm brutal-press rounded-xl bg-brand-violet text-white py-2.5 font-bold text-sm flex items-center justify-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" strokeWidth={2.5} />}AI
        </button>
      </div>
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { uploadMany(e.target.files); e.target.value = ""; }} />
      <input ref={cameraRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => { uploadMany(e.target.files); e.target.value = ""; }} />
    </div>
  );
}

export function initials(name = "") {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (p.length === 0) return "?";
  if (p.length === 1) return p[0].slice(0, 2).toUpperCase();
  return (p[0][0] + p[p.length - 1][0]).toUpperCase();
}
